import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardFooter,
  CCardText,
  CCardTitle,
  CCol,
  CCollapse,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CInputGroup,
  CInputGroupText,
  CMultiSelect,
  CPagination,
  CPaginationItem,
  CRow,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import {
  cilChevronBottom,
  cilFilter,
  cilPlus,
  cilSearch,
} from "@coreui/icons";
import {
  byStatDescending,
  byStatMagnitudeAscending,
  parseBillingCycle,
  priceForCycle,
  useTradingStrategies,
  type BillingCycle,
  type TradingStrategyDto,
  isFreeStrategy,
} from "../../api/hooks/useTradingStrategies";
import {
  isActiveSubscription,
  useMySubscriptions,
} from "../../api/hooks/useStrategySubscriptions";
import {
  isIncludedInMembership,
  useMyMemberships,
} from "../../api/hooks/useMemberships";
import { useMembershipsEnabled } from "../../api/hooks/usePortalSettings";
import { MembershipBanner } from "./components/MembershipBanner";
import { BacktestNote } from "./components/BacktestNote";
import { formatPriceUsd } from "../../lib/money";
import { StrategyIdentityBand } from "./components/StrategyIdentityBand";
import { StrategyCurve } from "./components/StrategyCurve";
import { StrategySidesTable } from "./components/StrategySidesTable";
import { groupSides, sideGroupKey } from "./strategySides";
import {
  activeFilterKeys,
  applyFilters,
  BOUND_FILTERS,
  clearFilter,
  EMPTY_FILTER_STATE,
  multiFilter,
  MULTI_FILTERS,
  type FilterReference,
  optionCounts,
  parseFilterState,
  tightestFilter,
  writeFilterState,
  type BoundFilterKey,
  type MultiFilterKey,
  type StrategyFilterState,
  type TextFilterKey,
} from "./strategyFilters";
import {
  useAssetClasses,
  useReferenceLabel,
  useRiskProfiles,
  useStrategyDirections,
  useTimeframes,
} from "../../api/hooks/useReference";
import { routePaths } from "../../routes/route-paths";
import { trackEvent } from "../../lib/telemetry";

const PAGE_SIZE = 9;

// Long enough that clicking through several options in a row reports where the customer stopped,
// not each step on the way.
const NO_RESULTS_SETTLE_MS = 800;

// Module scope, so a combination is reported once per visit however often it is returned to.
const reportedEmptyFilters = new Set<string>();

type SortKey =
  "avgAnnualProfit" | "winRate" | "maxDD" | "priceAsc" | "priceDesc";

const DEFAULT_SORT: SortKey = "winRate";

const SORT_COMPARATORS: Record<
  SortKey,
  (a: TradingStrategyDto, b: TradingStrategyDto) => number
> = {
  // On the published figures, not on the rendered text. A stat a strategy does not carry sorts
  // last rather than as a zero, which would place it among the genuinely poor performers.
  avgAnnualProfit: byStatDescending("Avg Annual Profit"),
  winRate: byStatDescending("Win Rate"),
  maxDD: byStatMagnitudeAscending("Max Real Drawdown"),
  priceAsc: (a, b) => a.monthlyPriceUsd - b.monthlyPriceUsd,
  priceDesc: (a, b) => b.monthlyPriceUsd - a.monthlyPriceUsd,
};

function isSortKey(value: string | null): value is SortKey {
  return (
    value === "avgAnnualProfit" ||
    value === "winRate" ||
    value === "maxDD" ||
    value === "priceAsc" ||
    value === "priceDesc"
  );
}

export function TradingStrategiesCoreui() {
  const { t } = useTranslation("tradingStrategies");
  const membershipsEnabled = useMembershipsEnabled();
  const navigate = useNavigate();
  const subscriptions = useMySubscriptions();
  // Memoised because everything downstream of it now is: an empty array built fresh each render
  // made the filter state, the counts and the culprit search all recompute on every keystroke.
  const subscriptionItems = useMemo(
    () => subscriptions.data ?? [],
    [subscriptions.data],
  );
  const memberships = useMyMemberships();
  const membershipItems = useMemo(
    () => memberships.data ?? [],
    [memberships.data],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: strategies = [],
    isLoading,
    error,
  } = useTradingStrategies();
  const pageLoading =
    isLoading || subscriptions.isLoading || memberships.isLoading;
  const pageError = error ?? subscriptions.error ?? memberships.error;

  // Holding either side of a strategy takes the whole strategy off the list. Held sides come from the
  // subscriptions, so one that has left the catalogue still counts.
  const allStrategies = useMemo(() => {
    const heldSides = subscriptionItems
      .filter((subscription) => isActiveSubscription(subscription))
      .map((subscription) => subscription.strategy);
    const includedSides = strategies.filter((tpl) =>
      isIncludedInMembership(tpl.id, membershipItems),
    );
    const takenStrategies = new Set(
      [...heldSides, ...includedSides].map(sideGroupKey),
    );

    return strategies.filter((tpl) => !takenStrategies.has(sideGroupKey(tpl)));
  }, [strategies, subscriptionItems, membershipItems]);

  // The filters offer every value the package defines, not just the ones the catalogue happens to
  // hold, so a value does not vanish when the last strategy using it is unpublished.
  const assetClasses = useAssetClasses().data;
  const timeframes = useTimeframes().data;
  const riskProfiles = useRiskProfiles().data;
  const strategyDirections = useStrategyDirections().data;
  const reference: FilterReference = useMemo(
    () => ({
      assetClass: assetClasses,
      timeframe: timeframes,
      riskProfile: riskProfiles,
      direction: strategyDirections,
    }),
    [assetClasses, timeframes, riskProfiles, strategyDirections],
  );

  const rawSort = searchParams.get("sort");
  const selectedSort: SortKey = isSortKey(rawSort) ? rawSort : DEFAULT_SORT;

  // In the URL beside the filters, so a shared link keeps the cycle it was shared in and a refresh
  // does not drop back to monthly halfway through comparing annual prices.
  const cycle = parseBillingCycle(searchParams.get("cycle"));

  const filters = useMemo(
    () => parseFilterState(searchParams, allStrategies, reference),
    [searchParams, allStrategies, reference],
  );
  const activeKeys = useMemo(() => activeFilterKeys(filters), [filters]);
  const anyFilterActive = activeKeys.length > 0;

  // Arriving on a filtered link with the panel shut shows a short list, a number in a badge and no
  // visible reason for either. So until someone works the toggle themselves, the panel simply
  // follows whether anything is filtered.
  //
  // Derived rather than initialised, because on the first render the catalogue has not arrived and
  // the filters — which are validated against the values it offers — all read as empty. Once
  // someone does work the toggle, their choice holds however the filters change afterwards.
  const [panelToggledOpen, setPanelToggledOpen] = useState<boolean | null>(
    null,
  );
  const panelOpen = panelToggledOpen ?? anyFilterActive;

  // Filters match sides. A card shows the sides that matched and sorts on the first of them.
  const filteredGroups = useMemo(() => {
    const matching = applyFilters(allStrategies, filters, reference);
    const groups = groupSides(matching);
    const comparator = SORT_COMPARATORS[selectedSort];

    return groups.sort((a, b) => comparator(a[0], b[0]));
  }, [allStrategies, filters, selectedSort, reference]);

  // Which single filter, dropped on its own, opens the results up the most — and nothing when every
  // one of them still leaves the list empty, because then no single filter is the problem.
  const culprit = useMemo(
    () =>
      filteredGroups.length === 0 && anyFilterActive
        ? tightestFilter(allStrategies, filters, reference)
        : null,
    [filteredGroups.length, anyFilterActive, allStrategies, filters, reference],
  );

  const emptyFilterQuery =
    !pageLoading && !pageError && filteredGroups.length === 0 && anyFilterActive
      ? writeFilterState(new URLSearchParams(), filters).toString()
      : null;
  const culpritKey = culprit?.key ?? "combination";
  const activeFilterCount = activeKeys.length;

  useEffect(() => {
    if (!emptyFilterQuery || reportedEmptyFilters.has(emptyFilterQuery)) return;
    const timer = setTimeout(() => {
      reportedEmptyFilters.add(emptyFilterQuery);
      trackEvent("StrategyListNoResults", {
        filters: emptyFilterQuery,
        culprit: culpritKey,
        activeFilterCount,
      });
    }, NO_RESULTS_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [emptyFilterQuery, culpritKey, activeFilterCount]);

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
  const requestedPage = Math.max(
    1,
    parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );
  const currentPage = Math.min(requestedPage, totalPages);

  useEffect(() => {
    if (requestedPage !== currentPage) {
      const next = new URLSearchParams(searchParams);
      if (currentPage <= 1) next.delete("page");
      else next.set("page", currentPage.toString());
      setSearchParams(next, { replace: true });
    }
  }, [requestedPage, currentPage, searchParams, setSearchParams]);

  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const visibleGroups = filteredGroups.slice(startIdx, startIdx + PAGE_SIZE);
  const endIdx = startIdx + visibleGroups.length;

  const goToPage = (page: number) => {
    const target = Math.min(Math.max(1, page), totalPages);
    if (target === currentPage) return;
    const next = new URLSearchParams(searchParams);
    if (target <= 1) next.delete("page");
    else next.set("page", target.toString());
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Carries what they were browsing into the request, so the form opens part-filled. Free text on
  // the other side, so several selections join rather than one being chosen for them.
  const requestStrategyPath = useMemo(() => {
    const params = new URLSearchParams();
    const carry = (key: MultiFilterKey, param: string) => {
      const chosen = filters.multi[key];
      if (chosen.length > 0) params.set(param, chosen.join(", "));
    };
    carry("assetClass", "assetClass");
    carry("timeframe", "timeframe");
    const query = params.toString();
    return query
      ? `${routePaths.requestStrategy}?${query}`
      : routePaths.requestStrategy;
  }, [filters.multi]);

  const applyFilterState = (next: StrategyFilterState) => {
    setSearchParams(writeFilterState(searchParams, next));
    // Changing a filter means the panel was in front of them, so hold it open. Without this,
    // clearing the last filter would make the panel they were working in shut itself.
    setPanelToggledOpen((toggled) => toggled ?? true);
  };

  const updateMulti = (key: MultiFilterKey, values: string[]) => {
    applyFilterState({
      ...filters,
      multi: { ...filters.multi, [key]: values },
    });
  };

  // Replaces rather than pushes, because this runs on a pause in typing rather than on a click —
  // pushed, a search would leave one history entry per pause for Back to walk out through. And it
  // does not touch the panel: the box sits outside it, so opening it would be a reply to nothing.
  const updateText = (key: TextFilterKey, value: string) => {
    setSearchParams(
      writeFilterState(searchParams, {
        ...filters,
        text: { ...filters.text, [key]: value },
      }),
      { replace: true },
    );
  };

  const updateBound = (key: BoundFilterKey, raw: string) => {
    applyFilterState({
      ...filters,
      bounds: { ...filters.bounds, [key]: raw === "" ? null : Number(raw) },
    });
  };

  const updateSort = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === DEFAULT_SORT) next.delete("sort");
    else next.set("sort", value);
    next.delete("page");
    setSearchParams(next);
  };

  // Display only — it does not touch the filters, the sort or what is in the cart, so unlike a
  // filter change it leaves the page number alone. Monthly is dropped from the URL rather than
  // written as the default, the same way the default sort is.
  const updateCycle = (next: BillingCycle) => {
    const params = new URLSearchParams(searchParams);
    if (next === "Monthly") params.delete("cycle");
    else params.set("cycle", next);
    setSearchParams(params);
  };

  const resetFilters = () => {
    applyFilterState(EMPTY_FILTER_STATE);
  };

  return (
    <div>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-2 mb-4">
        <div>
          <h2 className="mb-0">{t("title")}</h2>
          <p className="text-body-secondary mb-0">{t("subtitle")}</p>
        </div>
        {/* Level with the title and full width on a phone, as every page header is. */}
        <div className="col-12 col-sm-auto d-grid">
          <CButton color="primary" onClick={() => navigate(requestStrategyPath)}>
            <CIcon icon={cilPlus} className="me-2" />
            {t("request.link")}
          </CButton>
        </div>
      </div>

      {membershipsEnabled && <MembershipBanner />}

      <CCard className="mb-4">
        <CCardBody>
          {/* Filtering changes which strategies you see; sorting changes the order of the ones you
              already have. So every filter lives behind the one toggle and sort stays outside it —
              which is also why nothing here is called "advanced".

              Two groups on one wrapping line rather than a stack: finding (search, the filter toggle,
              Clear all) and showing (price cycle, sort). Each group states the width below which it
              would rather take a line of its own, so the toolbar is one row on a desktop and two on a
              phone without a breakpoint deciding it. Stacked — which is what this was — every control
              took a full-width row of its own, and four of those is most of a phone screen before a
              single strategy. */}
          <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
            <div
              className="d-flex align-items-center gap-2 flex-grow-1"
              style={{ minWidth: "min(100%, 20rem)" }}
            >
              {/* First and widest, and outside the collapse unlike every other filter. The panel is
                  shut until something is filtered, and a search nobody can see is a search nobody
                  uses — this is the one filter a customer arrives already knowing they want. It
                  still counts in the badge and clears with the rest, being in the same state. */}
              <NameSearchControl
                value={filters.text.name}
                disabled={pageLoading}
                onChange={(value) => updateText("name", value)}
              />
              <CButton
                color="primary"
                variant={anyFilterActive ? undefined : "outline"}
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                onClick={() => setPanelToggledOpen(!panelOpen)}
                aria-expanded={panelOpen}
                aria-controls="strategy-filters"
                aria-label={t("filters.title")}
                disabled={pageLoading}
              >
                {/* The word goes on a phone and the icon carries it, which is what buys the search its
                    width. The badge stays at every size: it is the only thing saying anything is on. */}
                <CIcon icon={cilFilter} className="me-sm-2" aria-hidden />
                <span className="d-none d-sm-inline">{t("filters.title")}</span>
                {anyFilterActive && (
                  <CBadge
                    color="light"
                    textColor="primary"
                    shape="rounded-pill"
                    className="ms-2"
                  >
                    {activeKeys.length}
                  </CBadge>
                )}
                <CIcon
                  icon={cilChevronBottom}
                  className="ms-2 d-none d-sm-inline"
                  aria-hidden
                  style={{
                    transform: panelOpen ? "rotate(180deg)" : undefined,
                    transition: "transform 0.15s",
                  }}
                />
              </CButton>

              {anyFilterActive && (
                <CButton
                  color="link"
                  className="px-0 text-body-secondary flex-shrink-0"
                  onClick={resetFilters}
                >
                  {t("filters.clearAll")}
                </CButton>
              )}
            </div>

            <div
              className="d-flex align-items-center gap-2 gap-md-3 flex-grow-1 flex-md-grow-0 ms-md-auto"
              style={{ minWidth: "min(100%, 24rem)" }}
            >
              {/* Display only, so it sits with sort rather than inside the filter panel — nothing here
                  changes which strategies you see. Same control as the one on the strategy page, so
                  the price a card was clicked at is chosen the same way on both. */}
              <div className="d-flex align-items-center gap-2">
                {/* Both labels go below lg, not just on a phone: their ~120px is the difference between
                    this group fitting beside the search and dropping to a line of its own, and a phone
                    held sideways lands squarely in that band. Neither control needs its label read —
                    the pills say Monthly and Yearly, the select says what it sorts by — and both still
                    name themselves to a screen reader. */}
                <span className="small text-body-secondary flex-shrink-0 d-none d-lg-inline">
                  {t("filters.showPrices")}
                </span>
                <CButtonGroup
                  className="btn-group-pill"
                  role="group"
                  aria-label={t("filters.showPrices")}
                >
                  <CButton
                    color="primary"
                    variant={cycle === "Monthly" ? undefined : "outline"}
                    size="sm"
                    aria-pressed={cycle === "Monthly"}
                    onClick={() => updateCycle("Monthly")}
                  >
                    {t("filters.monthly")}
                  </CButton>
                  <CButton
                    color="primary"
                    variant={cycle === "Yearly" ? undefined : "outline"}
                    size="sm"
                    aria-pressed={cycle === "Yearly"}
                    onClick={() => updateCycle("Yearly")}
                  >
                    {t("filters.yearly")}
                  </CButton>
                </CButtonGroup>
              </div>

              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <CFormLabel
                  htmlFor="filter-sort"
                  className="small text-body-secondary mb-0 flex-shrink-0 d-none d-lg-inline"
                >
                  {t("filters.sortBy")}
                </CFormLabel>
                <CFormSelect
                  id="filter-sort"
                  value={selectedSort}
                  onChange={(e) => updateSort(e.target.value)}
                  disabled={pageLoading}
                  aria-label={t("filters.sortBy")}
                  className="flex-grow-1"
                  style={{ maxWidth: "16rem" }}
                >
                  <option value="avgAnnualProfit">
                    {t("sort.avgAnnualProfit")}
                  </option>
                  <option value="winRate">{t("sort.winRate")}</option>
                  <option value="maxDD">{t("sort.maxDD")}</option>
                  <option value="priceAsc">{t("sort.priceAsc")}</option>
                  <option value="priceDesc">{t("sort.priceDesc")}</option>
                </CFormSelect>
              </div>
            </div>
          </div>

          <CCollapse visible={panelOpen}>
            <div id="strategy-filters" className="pt-4">
              <div
                className="small fw-bold text-uppercase text-body-secondary mb-2"
                style={{ letterSpacing: "0.08em" }}
              >
                {t("filters.groupStrategy")}
              </div>
              <CRow xs={{ gutter: 3 }} className="align-items-start mb-4">
                {MULTI_FILTERS.map((definition) => (
                  // Halves then quarters: four controls divide evenly at both widths, where thirds
                  // would leave the fourth orphaned on a row of its own.
                  <CCol
                    key={definition.key}
                    md={definition.growsWithSelection ? 12 : 6}
                    xl={definition.growsWithSelection ? 12 : 3}
                  >
                    <MultiFilterControl
                      filterKey={definition.key}
                      strategies={allStrategies}
                      filters={filters}
                    reference={reference}
                      disabled={pageLoading}
                      onChange={(values) => updateMulti(definition.key, values)}
                    />
                  </CCol>
                ))}
              </CRow>

              <div
                className="small fw-bold text-uppercase text-body-secondary mb-2"
                style={{ letterSpacing: "0.08em" }}
              >
                {t("filters.groupPerformance")}
              </div>
              <CRow xs={{ gutter: 3 }} className="align-items-start">
                {BOUND_FILTERS.map((definition) => (
                  <CCol key={definition.key} md={6} xl={3}>
                    <CFormLabel
                      htmlFor={`filter-${definition.key}`}
                      className="small text-body-secondary mb-1"
                    >
                      {t(`filters.${definition.key}`)}
                    </CFormLabel>
                    <CFormSelect
                      id={`filter-${definition.key}`}
                      value={filters.bounds[definition.key] ?? ""}
                      onChange={(e) =>
                        updateBound(definition.key, e.target.value)
                      }
                      disabled={pageLoading}
                    >
                      <option value="">{t("filters.any")}</option>
                      {definition.choices.map((choice) => (
                        <option key={choice} value={choice}>
                          {t(`filters.choices.${definition.key}`, {
                            value: choice,
                          })}
                        </option>
                      ))}
                    </CFormSelect>
                  </CCol>
                ))}
              </CRow>
            </div>
          </CCollapse>
        </CCardBody>
      </CCard>

      {pageLoading && (
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      )}

      {pageError && (
        <CAlert color="danger">
          {pageError instanceof Error
            ? pageError.message
            : "Unable to load strategies."}
        </CAlert>
      )}

      {/* "You have six filters on" is a number, not something anyone can act on. Where one filter
          alone is holding the list at nothing, name it and offer to drop it. Where no single one is
          — every removal still leaves nothing — say that instead of naming a filter whose removal
          would change nothing. */}
      {!pageLoading && !pageError && filteredGroups.length === 0 && (
        <CCard className="mb-4">
          <CCardBody className="text-center py-5">
            <h5 className="mb-2">{t("filters.noResults")}</h5>
            <p className="text-body-secondary mb-4">
              {culprit
                ? t("filters.noResultsCulprit", {
                    count: activeKeys.length,
                    filter: t(`filters.${culprit.key}`).toLocaleLowerCase(),
                    remaining: culprit.remaining,
                  })
                : t("filters.noResultsCombination", {
                    count: activeKeys.length,
                  })}
            </p>
            <div className="d-flex justify-content-center gap-2">
              {culprit && (
                <CButton
                  color="primary"
                  onClick={() =>
                    applyFilterState(clearFilter(filters, culprit.key))
                  }
                >
                  {t("filters.relaxThatOne")}
                </CButton>
              )}
              <CButton
                color="secondary"
                variant="outline"
                onClick={resetFilters}
              >
                {t("filters.clearFilters")}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      )}

      {!pageLoading && !pageError && filteredGroups.length > 0 && (
        <p className="small text-body-secondary mb-2">
          Showing <strong>{startIdx + 1}</strong>-<strong>{endIdx}</strong> of{" "}
          <strong>{filteredGroups.length}</strong> strategies
        </p>
      )}

      {!pageLoading && !pageError && filteredGroups.length > 0 && (
        <CRow xs={{ gutter: 4 }}>
          {visibleGroups.map((sides) => (
            <CCol key={sides[0].id} md={6} xl={4}>
              <StrategyCard sides={sides} cycle={cycle} />
            </CCol>
          ))}
        </CRow>
      )}

      {/* Way in two: an offer rather than an apology, where someone who has read the list and not
          found it is already looking. Deliberately outside the empty state, which WI #369 owns.
          Stacks on a phone with the button taking the width, the same as the header and the filter
          row - at its natural width it left a short button against a long line of dead space. */}
      {!pageLoading && !pageError && (
        <div className="strategy-ask d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mt-4 p-3 rounded">
          <div>
            <div className="fw-semibold">{t("request.strip.title")}</div>
            <div className="small text-body-secondary">
              {t("request.strip.body")}
            </div>
          </div>
          <CButton
            color="primary"
            className="flex-shrink-0"
            onClick={() => navigate(requestStrategyPath)}
          >
            {t("request.strip.action")}
          </CButton>
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <CPagination aria-label="Strategy pagination">
            <CPaginationItem
              aria-label="Previous"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              «
            </CPaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNum) => (
                <CPaginationItem
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => goToPage(pageNum)}
                >
                  {pageNum}
                </CPaginationItem>
              ),
            )}
            <CPaginationItem
              aria-label="Next"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              »
            </CPaginationItem>
          </CPagination>
        </div>
      )}

      {/* Once for the whole grid. Per card it was three rows of identical text crowding a
          sparkline that had no height to spare. */}
      {!pageLoading && !pageError && filteredGroups.length > 0 && <BacktestNote />}
    </div>
  );
}

interface NameSearchControlProps {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

/**
 * Free-text search on the strategy name.
 *
 * The box holds what is being typed and the URL is written on a pause. Straight through to the URL,
 * every keystroke would re-run the option counts across the catalogue and land a history entry; held
 * only in the box, a shared link would not carry the search and Clear all could not empty it.
 *
 * Grows to whatever the toolbar row leaves it, which on a phone is everything the filter toggle does
 * not take.
 */
function NameSearchControl({ value, disabled, onChange }: NameSearchControlProps) {
  const { t } = useTranslation("tradingStrategies");
  const [typed, setTyped] = useState(value);

  // Held in a ref so a re-render for any other reason does not restart the pause mid-word.
  const commit = useRef(onChange);
  useEffect(() => {
    commit.current = onChange;
  });

  // Compared trimmed, because the URL carries the trimmed term: compared raw, a trailing space would
  // come straight back as a correction and eat itself as the customer typed it.
  useEffect(() => {
    if (value.trim() === typed.trim()) return;
    setTyped(value);
    // Only when the URL moved under it — Clear all, a shared link, Back. Typing is the other
    // direction and is settled by the timer below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (typed.trim() === value.trim()) return;
    const timer = setTimeout(() => commit.current(typed), 250);
    return () => clearTimeout(timer);
  }, [typed, value]);

  return (
    <CInputGroup className="flex-grow-1">
      <CInputGroupText>
        <CIcon icon={cilSearch} aria-hidden />
      </CInputGroupText>
      <CFormInput
        id="filter-name"
        type="search"
        value={typed}
        disabled={disabled}
        onChange={(event) => setTyped(event.target.value)}
        placeholder={t("filters.namePlaceholder")}
        aria-label={t("filters.name")}
      />
    </CInputGroup>
  );
}

interface MultiFilterControlProps {
  filterKey: MultiFilterKey;
  strategies: TradingStrategyDto[];
  filters: StrategyFilterState;
  reference: FilterReference;
  disabled: boolean;
  onChange: (values: string[]) => void;
}

/**
 * One of the "any of these" filters, as a multi-select.
 *
 * Each option carries how many strategies it would return under whatever else is already on, so you
 * can see that picking Momentum alongside a daily timeframe leaves nothing before you spend the
 * click. Counts ignore this filter's own selection — otherwise every unticked option reads zero.
 */
function MultiFilterControl({
  filterKey,
  strategies,
  filters,
  reference,
  disabled,
  onChange,
}: MultiFilterControlProps) {
  const { t } = useTranslation("tradingStrategies");
  const definition = multiFilter(filterKey);
  const selected = filters.multi[filterKey];
  const toLabel = useReferenceLabel();
  const list = reference[filterKey];

  const options = useMemo(() => {
    if (!definition.available) return [];

    const counts = optionCounts(strategies, filters, definition, reference);

    // The option's value is the member name, which is what the URL carries; the label is translated,
    // falling back to the server's English. Symbols carry no reference list, so they label as-is.
    return definition.optionsOf(strategies, list ?? []).map((value) => {
      const match = list?.find((entry) => entry.name === value);

      return {
        value,
        label: match ? toLabel(match) : value,
        count: counts.get(value) ?? 0,
        selected: selected.includes(value),
      };
    });
  }, [definition, strategies, filters, selected, reference, list, toLabel]);

  return (
    <>
      <CFormLabel
        htmlFor={`filter-${filterKey}`}
        className="small text-body-secondary mb-1 d-flex align-items-center gap-2"
      >
        {t(`filters.${filterKey}`)}
        {!definition.available && (
          <CBadge color="warning" shape="rounded-pill" className="fw-normal">
            {t("filters.notCapturedYet")}
          </CBadge>
        )}
      </CFormLabel>
      <CMultiSelect
        id={`filter-${filterKey}`}
        options={options}
        // Held by the URL rather than by the control, so a shared link restores the selection and
        // Clear all can empty every filter from one place.
        value={selected}
        disabled={disabled || !definition.available}
        multiple
        search
        cleaner
        selectionType="tags"
        optionsStyle="checkbox"
        // The symbol list is eight long today and has every reason to grow; the scroller means the
        // number of them never becomes a design problem.
        virtualScroller
        visibleItems={10}
        placeholder={t("filters.any")}
        searchNoResultsLabel={t("filters.noMatchingOptions")}
        optionsTemplate={(option) => (
          <span className="d-flex justify-content-between align-items-center gap-3 w-100">
            <span>{option.label}</span>
            <span className="text-body-secondary small">
              {option.count as number}
            </span>
          </span>
        )}
        onChange={(chosen) => {
          const values = chosen.map((option) => option.value.toString());
          // CoreUI raises onChange while it settles on mount as well as on a real selection. Writing
          // that straight to the URL would clear a filter the customer arrived with.
          const unchanged =
            values.length === selected.length &&
            values.every((value) => selected.includes(value));
          if (!unchanged) onChange(values);
        }}
      />
    </>
  );
}

interface StrategyCardProps {
  /** The strategy's sides, in side order. The first is the one the card's curve and link show. */
  sides: TradingStrategyDto[];
  /** Which price to show, and which the side's page opens on. */
  cycle: BillingCycle;
}

function StrategyCard({ sides, cycle }: StrategyCardProps) {
  const { t } = useTranslation("tradingStrategies");
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const face = sides[0];
  // Carried through, so the headline on the strategy page is the same figure as the card that was
  // clicked. Monthly is the default at the other end and stays out of the URL.
  const cycleQuery = cycle === "Yearly" ? "?cycle=Yearly" : "";
  const detailPath = `/strategies/${face.id}${cycleQuery}`;

  const cardStyle: React.CSSProperties = {
    cursor: "pointer",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    transform: hovered ? "translateY(-2px)" : "none",
    boxShadow: hovered ? "0 0.5rem 1rem rgba(0,0,0,0.08)" : undefined,
    overflow: "hidden",
  };

  const handleCardActivate = () => navigate(detailPath);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardActivate();
    }
  };

  return (
    <CCard
      className="h-100"
      style={cardStyle}
      role="button"
      tabIndex={0}
      onClick={handleCardActivate}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`View details for ${face.publicName}`}
    >
      <StrategyIdentityBand strategy={face} />
      <CCardBody className="d-flex flex-column">
        <CCardTitle>{face.publicName}</CCardTitle>
        <CCardText
          className="text-body-secondary"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {face.description}
        </CCardText>
        <div className="mt-auto">
          <StrategyCurve strategy={face} emphasised={hovered} />
        </div>
      </CCardBody>
      <CCardFooter className="bg-body-tertiary">
        {/* Each side opens its own page, where it is bought, so the figures are seen before the cart. */}
        <StrategySidesTable
          sides={sides}
          endColumns={[
            {
              width: "3rem",
              heading: cycle === "Monthly" ? t("card.sides.perMonth") : t("card.sides.perYear"),
              cell: (side) =>
                isFreeStrategy(side) ? t("price.free") : formatPriceUsd(priceForCycle(side, cycle)),
            },
          ]}
          sideHref={(side) => `/strategies/${side.id}${cycleQuery}`}
        />
      </CCardFooter>
    </CCard>
  );
}
